/**
 * Grammar Check Service with Provider Pattern
 * Maintains backward compatibility while supporting multiple providers
 */

import type { GrammarError } from '$lib/types/grammar'
import type { IGrammarCheckProvider, ProviderConfig, ProviderType } from './providers/types'
import { grammarActions } from '$lib/stores/grammar'
import { SubscriptionService } from './subscription/SubscriptionService'
import { ProviderFactory } from './providers/ProviderFactory'
import { usageTracker } from './UsageTrackingService'
import { openUpgradePrompt } from '$lib/stores/subscription'
import { settingsActions, getSettings } from '$lib/stores/settings'
import { GrammarErrorFactory } from './GrammarErrorFactory'
import { batchCheckService, type BatchProgress } from '../../services/batchCheckService'
import type { CheckError } from '../../types/grammar'

export class GrammarCheckServiceV3 {
  private checkTimeout: NodeJS.Timeout | null = null
  private currentProvider: IGrammarCheckProvider | null = null
  private abortController: AbortController | null = null

  /**
   * Get current provider configuration from settings
   */
  private getCurrentProviderConfig(): ProviderConfig {
    const settings = getSettings()
    
    // Check if custom server is enabled
    if (settings.api?.customServer?.enabled) {
      // Determine provider type based on settings
      const providerType = settings.api.customServer.providerType || 'bareun'
      
      if (providerType === 'naraSpeller') {
        return {
          type: 'naraSpeller',
          apiKey: settings.api.customServer.apiKey,
          options: {
            weakOpt: settings.api.customServer.weakOpt ?? 0
          }
        }
      } else {
        // Default to Bareun for backward compatibility
        return {
          type: 'bareun',
          serverUrl: settings.api.customServer.url,
          apiKey: settings.api.customServer.apiKey
        }
      }
    }
    
    // Default to proxy if no custom server
    return { type: 'proxy' }
  }

  /**
   * Initialize or update the current provider
   */
  private ensureProvider(): IGrammarCheckProvider {
    const config = this.getCurrentProviderConfig()

    // Check if we need to update the provider
    if (!this.currentProvider || this.currentProvider.getType() !== config.type) {
      this.currentProvider = ProviderFactory.getProvider(config)
    } else {
      // Update existing provider configuration
      this.currentProvider.initialize(config)
    }

    return this.currentProvider
  }

  /**
   * Check if user is using their own custom provider (not server proxy)
   * Similar to BYOK for AI - users pay for their own resources
   */
  private isUsingCustomProvider(): boolean {
    const config = this.getCurrentProviderConfig()

    // If using custom server (bareun or naraSpeller with own API key) → true
    // If using proxy (server-provided) → false
    return config.type !== 'proxy'
  }

  /**
   * Check text for grammar errors
   * Maintains backward compatibility with existing interface
   */
  async checkText(text: string, onProgress?: (progress: BatchProgress) => void): Promise<GrammarError[]> {
    // Cancel any ongoing check
    this.cancel()

    // Create new abort controller for this request
    this.abortController = new AbortController()

    const subscriptionService = SubscriptionService.getInstance()
    const subscription = subscriptionService.getSubscription()
    const tier = subscription.type || 'free'

    // Rate limiting logic (similar to AI BYOK):
    // 1. Premium/Beta users → Unlimited (server-provided resources)
    // 2. Custom provider users → Unlimited (their own API key/resources)
    // 3. Free/Trial users with proxy → Rate limited (server resources)

    const isPremiumOrBeta = tier === 'premium' || tier === 'beta'
    const isCustomProvider = this.isUsingCustomProvider()

    // Only check limits for Free/Trial users using server proxy
    if (!isPremiumOrBeta && !isCustomProvider) {
      // Check subscription limits
      const canPerform = await subscriptionService.canPerformAction('spellCheck')
      if (!canPerform) {
        openUpgradePrompt('limit_reached', '맞춤법 검사 일일 한도 도달')
        throw new Error('Daily usage limit reached')
      }

      // Check text length limit
      if (!subscriptionService.isWithinTextLimit(text.length)) {
        openUpgradePrompt('text_too_long', text.length.toString())
        throw new Error('Text too long for free tier')
      }
    }

    const startTime = Date.now()
    const provider = this.ensureProvider()
    const providerType = provider.getType()
    
    
    // Check if batch processing is needed
    const needsBatch = batchCheckService.needsBatchProcessing(text, providerType)
    
    if (needsBatch) {
      // Use batch processing for long texts
      return await this.checkTextWithBatch(text, providerType, onProgress)
    }
    
    try {
      // Use the provider to check text normally with abort signal
      const errors = await provider.checkText(text, { signal: this.abortController?.signal })
      
      // Track usage (same as before)
      const responseTime = Date.now() - startTime
      await usageTracker.trackGrammarCheck({
        textLength: text.length,
        errorCount: errors.length,
        responseTime,
        source: provider.getType()
      })
      
      // Remove duplicates
      const uniqueErrors = this.removeDuplicates(errors)

      // Record successful usage for subscription tracking
      // Only track usage for Free/Trial users using server proxy
      const isPremiumOrBeta = tier === 'premium' || tier === 'beta'
      const isCustomProvider = this.isUsingCustomProvider()

      if (!isPremiumOrBeta && !isCustomProvider) {
        await subscriptionService.recordUsage('spellCheck')
      }

      return uniqueErrors
    } catch (error) {
      console.error('Grammar check failed:', error)

      // Track failed attempt
      const responseTime = Date.now() - startTime
      await usageTracker.trackGrammarCheck({
        textLength: text.length,
        errorCount: 0,
        responseTime,
        source: provider.getType()
      })

      // Always throw rate limit errors (429) to show proper modal
      const errorMessage = error instanceof Error ? error.message : String(error)
      if (errorMessage.includes('429') || errorMessage.includes('Too Many Requests')) {
        throw error
      }

      // Fallback to mock data in development for other errors
      if (import.meta.env.DEV) {
        return this.getMockErrors(text)
      }

      throw error
    }
  }

  /**
   * Check text using batch processing for long documents
   */
  private async checkTextWithBatch(
    text: string, 
    providerType: ProviderType,
    onProgress?: (progress: BatchProgress) => void
  ): Promise<GrammarError[]> {
    const startTime = Date.now()
    
    
    try {
      // Get provider config to pass API key and options
      const providerConfig = this.getCurrentProviderConfig()
      
      // Start batch job with provider config and abort signal
      const job = await batchCheckService.startBatchCheck(
        text, 
        providerType, 
        providerConfig.apiKey, 
        providerConfig.options,
        this.abortController?.signal
      )
      
      
      // Subscribe to progress if callback provided
      let unsubscribe: (() => void) | null = null
      if (onProgress) {
        unsubscribe = batchCheckService.subscribeToProgress(job.jobId, providerType, onProgress)
      }
      
      // Poll for completion
      const result = await batchCheckService.pollForCompletion(job.jobId, providerType, (progress) => {
        if (onProgress) onProgress(progress)
      })
      
      // Unsubscribe from progress
      if (unsubscribe) {
        unsubscribe()
      }
      
      
      if (!result.success || !result.errors) {
        throw new Error(result.error || 'Batch check failed')
      }
      
      // Convert CheckError to GrammarError
      const errors = this.convertCheckErrorsToGrammarErrors(result.errors)
      
      // Track usage
      const responseTime = Date.now() - startTime
      await usageTracker.trackGrammarCheck({
        textLength: text.length,
        errorCount: errors.length,
        responseTime,
        source: `batch-${providerType}`
      })
      
      // Remove duplicates
      const uniqueErrors = this.removeDuplicates(errors)

      // Record successful usage for subscription tracking
      // Only track usage for Free/Trial users using server proxy
      const subscriptionService = SubscriptionService.getInstance()
      const subscription = subscriptionService.getSubscription()
      const tier = subscription.type || 'free'
      const isPremiumOrBeta = tier === 'premium' || tier === 'beta'
      const isCustomProvider = this.isUsingCustomProvider()

      if (!isPremiumOrBeta && !isCustomProvider) {
        await subscriptionService.recordUsage('spellCheck')
      }

      return uniqueErrors
    } catch (error) {
      console.error('Batch check failed:', error)
      
      // Track failed attempt
      const responseTime = Date.now() - startTime
      await usageTracker.trackGrammarCheck({
        textLength: text.length,
        errorCount: 0,
        responseTime,
        source: `batch-${providerType}`
      })
      
      throw error
    }
  }

  /**
   * Convert CheckError array to GrammarError array
   */
  private convertCheckErrorsToGrammarErrors(checkErrors: CheckError[]): GrammarError[] {
    return checkErrors.map((error, index) => 
      GrammarErrorFactory.createUnified({
        id: `batch-${index}`,
        type: 'grammar',
        level: 'error',
        position: { start: error.startPos || 0, end: error.endPos || 0 },
        original: error.orgStr || '',
        suggestions: error.candWord || [],
        message: error.description || error.message || error.help || '문법 오류',
        help: error.help || ''
      })
    )
  }

  /**
   * Test current provider connection
   */
  async testConnection(): Promise<{ success: boolean; message: string; responseTime?: number }> {
    try {
      const provider = this.ensureProvider()
      return await provider.testConnection()
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : '연결 테스트 실패'
      }
    }
  }

  /**
   * Get current provider information
   */
  getCurrentProvider(): { type: ProviderType; name: string; description: string } {
    const provider = this.ensureProvider()
    return {
      type: provider.getType(),
      name: provider.getName(),
      description: provider.getDescription()
    }
  }
  
  /**
   * Cancel any ongoing grammar check
   */
  cancel(): void {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
    // Also cancel any batch processing
    batchCheckService.cancelAll()
  }
  
  /**
   * Remove duplicate errors based on position and content
   */
  private removeDuplicates(errors: GrammarError[]): GrammarError[] {
    // Use position as primary key for deduplication
    const positionMap = new Map<string, GrammarError>()
    
    for (const error of errors) {
      const startPos = error.position?.start ?? (error as any).startPos
      const endPos = error.position?.end ?? (error as any).endPos
      const originalText = error.original || error.originalText || ''
      
      // Skip if no position information
      if (startPos === undefined || endPos === undefined) {
        continue
      }
      
      // Use position as unique key
      const posKey = `${startPos}-${endPos}`
      
      // Check if we already have an error at this position
      const existing = positionMap.get(posKey)
      
      if (!existing) {
        // First error at this position
        positionMap.set(posKey, error)
      } else {
        // Already have an error at this position - keep the better one
        const existingSuggestions = existing.suggestions?.length || 0
        const currentSuggestions = error.suggestions?.length || 0
        const existingInfo = (existing.message?.length || 0) + (existing.help?.length || 0)
        const currentInfo = (error.message?.length || 0) + (error.help?.length || 0)
        
        // Prioritize: more suggestions > more information > keep existing
        if (currentSuggestions > existingSuggestions || 
            (currentSuggestions === existingSuggestions && currentInfo > existingInfo)) {
          positionMap.set(posKey, error)
        }
      }
    }
    
    // Return array sorted by position
    return Array.from(positionMap.values()).sort((a, b) => {
      const aStart = a.position?.start ?? (a as any).startPos ?? 0
      const bStart = b.position?.start ?? (b as any).startPos ?? 0
      return aStart - bStart
    })
  }
  
  /**
   * Get mock errors for development
   * (Keeping original implementation)
   */
  private getMockErrors(text: string): GrammarError[] {
    const errors: GrammarError[] = []
    
    // Mock error detection
    if (text.includes('않 습니다')) {
      const startPos = text.indexOf('않 습니다')
      errors.push(GrammarErrorFactory.createUnified({
        id: 'mock-spacing-1',
        type: 'grammar',
        level: 'error',
        position: { start: startPos, end: startPos + 6 },
        original: '않 습니다',
        suggestions: ['않습니다'],
        message: '띄어쓰기 오류',
        help: '보조 용언은 앞말에 붙여 씁니다.'
      }))
    }
    
    return errors
  }

  /**
   * Check if user can perform grammar check based on rate limits and subscription
   * (Keeping original implementation)
   */
  async canPerformCheck(): Promise<boolean> {
    const subscriptionService = SubscriptionService.getInstance()
    return await subscriptionService.canPerformAction('spellCheck')
  }
  
  /**
   * Force refresh rate limit status
   * (Keeping original implementation)
   */
  async refreshRateLimitStatus(): Promise<boolean> {
    try {
      const result = await usageTracker.checkRateLimit('grammar')
      return result.allowed
    } catch (error) {
      console.error('Failed to refresh rate limit status:', error)
      return true // Allow on error
    }
  }

  /**
   * Apply a correction to the text
   * (Keeping original implementation)
   */
  applyCorrection(errorId: string, correctionId: string): void {
    grammarActions.applyCorrection(errorId, correctionId)
  }

  /**
   * Apply all available corrections
   * (Keeping original implementation)
   */
  applyAllCorrections(): string {
    return grammarActions.applyAllCorrections()
  }

  /**
   * Perform debounced grammar check
   * (Keeping original implementation)
   */
  debouncedCheck(text: string, delay: number = 500): void {
    if (this.checkTimeout) {
      clearTimeout(this.checkTimeout)
    }
    
    this.checkTimeout = setTimeout(async () => {
      const errors = await this.checkText(text)
      grammarActions.setErrors(errors)
    }, delay)
  }

  /**
   * Cancel pending check
   * (Keeping original implementation)
   */
  cancelPendingCheck(): void {
    if (this.checkTimeout) {
      clearTimeout(this.checkTimeout)
      this.checkTimeout = null
    }
  }

  /**
   * Clean up resources
   * (Keeping original implementation)
   */
  destroy(): void {
    this.cancelPendingCheck()
  }
}

// Export singleton instance (backward compatibility)
export const grammarCheckService = new GrammarCheckServiceV3()